// src/resources/reports/ReportPushNotifications/HourlyChart.tsx
//
// "Melhor horário" (§4 bloco 5) — barras de volume enviado por hora (0–23) +
// linha de taxa de leitura, pra decidir quando disparar a próxima campanha.
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import { HourlyPoint, fmtAxisPct, fmtInt, fmtPct, niceRateDomainMax } from './types';

const BLUE = '#3b82f6';
const INK = '#0f172a';

const HourlyChart = ({ hourly }: { hourly: HourlyPoint[] }) => {
  // Backend já manda 0–23, mas preenche os buracos aqui — hora sem disparo não
  // pode sumir do eixo, senão o gráfico mente sobre "nunca disparamos às 4h".
  const byHour = useMemo(() => new Map(hourly.map(h => [h.hour, h])), [hourly]);
  const chartData = useMemo(() => Array.from({ length: 24 }, (_, hour) => {
    const point = byHour.get(hour);
    return {
      hour,
      label: `${String(hour).padStart(2, '0')}h`,
      sent: point?.sent ?? 0,
      readRatePct: point?.readRate !== null && point?.readRate !== undefined ? point.readRate * 100 : null,
    };
  }), [byHour]);

  const rateAxisMax = useMemo(() => niceRateDomainMax(chartData.map(d => d.readRatePct)), [chartData]);

  const hasData = hourly.some(h => h.sent > 0);

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Melhor horário</Typography>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
        Volume enviado e taxa de leitura por hora do dia — ajuda a escolher quando disparar
      </Typography>
      {!hasData ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Sem envio no período.</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10.5 }} tickLine={false} interval={1} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => fmtInt(v)} />
              {/* Escala da taxa vem dos dados (niceRateDomainMax): fixa em 0–100% a linha de
                  leitura, que fica abaixo de 2%, virava um risco colado no eixo. */}
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={52}
                domain={[0, rateAxisMax]} tickFormatter={(v: number) => fmtAxisPct(v, rateAxisMax)} />
              <ReTooltip
                labelFormatter={(label: any) => String(label)}
                // null = hora sem envio: mostra travessão, não "0,0%" (0% medido e "sem dado"
                // são coisas diferentes pra quem está escolhendo horário de disparo).
                formatter={(value: any, name: any) => (name === 'Taxa de leitura'
                  ? [value === null || value === undefined ? '—' : fmtPct(Number(value) / 100, 1), name]
                  : [fmtInt(Number(value)), name])}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
              <Bar yAxisId="left" dataKey="sent" name="Enviadas" fill={BLUE} radius={[2, 2, 0, 0]} maxBarSize={22} />
              <Line yAxisId="right" dataKey="readRatePct" name="Taxa de leitura" stroke={INK} strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default HourlyChart;
